import React, { ReactNode } from 'react';
import { useGetContentListQuery } from '@api/api';
import { ContentRequest, EpiBlock } from '@api/types';
import { useEditMode, useEditModeLinks } from '@hooks';
import { Settings } from '@mui/icons-material';
import { Box, Divider, Link, Menu, MenuItem, styled, Typography } from '@mui/material';
import { getContentAreaIds, isContentArea } from '@utils/content.utils';
import { camel2title } from '@utils/text.utils';

const BlockEditBoxWrapper = styled(Box)`
  position: relative;
  &::after {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.3);
    content: ' ';
    display: none;
    pointer-events: none;
  }
  &:hover::after {
    display: block;
  }
  .edit-button {
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
    padding: 4px;
    font-size: 32px;
    z-index: 999;
    background: rgba(0, 0, 0, 0.4);
    color: #fff;
  }
  &.epi-injected-minSize-inline {
    display: block !important;
  }
`;

const Title = styled(Typography)`
  padding: 0 16px;
  font-weight: 800;
`;

export type BlockEditorOptionsProps = {
  children?: ReactNode;
  id?: number;
  title?: string;
  enableContextMenu?: boolean;
  enableNativeOverlay?: boolean;
  block?: EpiBlock;
};

/**
 * Wraps block in box with 'Edit' button or native overlay
 */
export const BlockEditorContextMenu = ({ children, id, title, block }: BlockEditorOptionsProps) => {
  const { inEditMode, inBlockEdit } = useEditMode();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return inEditMode ? (
    <BlockEditBoxWrapper>
      <Settings onClick={handleClick} className='edit-button' />
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {!inBlockEdit && <Title>{title || 'Block'}</Title>}
        {!inBlockEdit && <EditLink id={id} title={title} />}
        <NestedAreaList block={block} />
        <Divider />
        <MenuItem onClick={handleClose}>Close menu</MenuItem>
      </Menu>
      {children}
    </BlockEditBoxWrapper>
  ) : (
    <>{children}</>
  );
};

type NestedAreaListProps = {
  block?: any;
};

const NestedAreaList = ({ block = {} }: NestedAreaListProps) => {
  const props = Object.entries(block).filter(([_, value]) => isContentArea(value));
  return (
    <>
      {props &&
        props.map(([title, blocks], key) => {
          return <NestedBlockList key={key} title={title} blocks={blocks as EpiBlock[]} />;
        })}
    </>
  );
};

type NestedBlockListProps = {
  title?: string;
  blocks?: Array<EpiBlock>;
};

const NestedBlockList = ({ title, blocks = [] }: NestedBlockListProps) => {
  const references = getContentAreaIds(blocks);
  const request: ContentRequest = {
    references,
    expand: '*',
  };
  const { data } = useGetContentListQuery(request);
  const getTitle = (id: number) => {
    const item = data?.find((x) => x.contentLink?.id === id);
    return item?.title || item?.displayName || item?.name || `block #${id}`;
  };
  return (
    <>
      <Title>Blocks in {camel2title(title || 'content')}</Title>
      {blocks &&
        blocks.map((block, key) => {
          const id = block.contentLink?.id || 0;
          return <EditLink key={key} id={id} title={getTitle(id)} />;
        })}
    </>
  );
};

type EditLinkProps = {
  id?: number;
  title?: string;
};

const EditLink = ({ id, title }: EditLinkProps) => {
  const { getEditContentLink } = useEditModeLinks();
  return (
    <MenuItem>
      <Link
        href={getEditContentLink(id)}
        target='_top'
        className='edit-button'
        sx={{ display: 'block' }}
      >
        Edit {title || `block #${id}`}
      </Link>
    </MenuItem>
  );
};
